import React, { useState, useEffect, useMemo, useRef } from "react";
import { PartyPopper, Cake, Award } from "lucide-react";
import {
  useGetCelebrationsQuery,
  IBirthdayItem,
  IAnniversaryItem,
} from "../../store/apis/user.api";
import { CARD_CLASS, DASHBOARD_COLORS } from "../../constants/dashboard";
import WishModal from "./WishModal";
import { getAvatarGradient, getInitials } from "../../utils/avatarUtils";

interface CelebrationItem {
  type: "birthday" | "anniversary";
  initials: string;
  name: string;
  userId: string;
  tag: string;
  tagIcon: typeof Cake;
  tagColor: string;
  bg: string;
  avatarIdx: number;
}

interface BirthdayAnniversaryCardProps {
  variant?: "carousel" | "list";
  title?: string;
  wishButtonText?: string;
  wishButtonRounded?: string;
  showWishEmoji?: boolean;
}

const BirthdayAnniversaryCard: React.FC<BirthdayAnniversaryCardProps> = ({
  variant = "carousel",
  title,
  wishButtonText,
  wishButtonRounded,
  showWishEmoji,
}) => {
  const isList = variant === "list";
  const buttonLabel = wishButtonText ?? (isList ? "Wish" : showWishEmoji ? "Wish 🎉" : "Wish");
  const buttonRadiusClass = wishButtonRounded ?? "rounded-md";

  const { data, isLoading: loading } = useGetCelebrationsQuery();
  const birthdays = data?.birthdays ?? [];
  const anniversaries = data?.anniversaries ?? [];

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
      return false;
    }
  }, []);

  const allCelebrations: CelebrationItem[] = useMemo(() => [
    ...birthdays.map((b, i) => ({
      type: "birthday" as const,
      initials: getInitials(b.name || "?"),
      name: b.name,
      userId: b.userId,
      tag: "Birthday today!",
      tagIcon: Cake,
      tagColor: "var(--color-event-birthday)",
      bg: "var(--color-event-birthday-bg)",
      avatarIdx: i,
    })),
    ...anniversaries.map((a, i) => ({
      type: "anniversary" as const,
      initials: getInitials(a.name || "?"),
      name: a.name,
      userId: a.userId,
      tag: `${a.yearsOfService} Year${a.yearsOfService > 1 ? "s" : ""}`,
      tagIcon: Award,
      tagColor: "var(--color-event-anniversary)",
      bg: "var(--color-event-anniversary-bg)",
      avatarIdx: i,
    })),
  ], [birthdays, anniversaries]);

  const [containerWidth, setContainerWidth] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (variant === "list") return;
    const el = rootRef.current;
    if (!el) return;
    const measure = () => setContainerWidth(el.offsetWidth);
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [loading, variant]);

  const effectivePerView = containerWidth > 0 ? (containerWidth < 480 ? 1 : containerWidth < 768 ? 2 : 3) : 1;
  const slideWidth = containerWidth > 0 ? Math.round(containerWidth / effectivePerView) : 0;

  // Step-and-pause motion: one card slides in, holds for DWELL_MS, then the
  // next slides in. The track holds two identical sets so the cycle can reset
  // to the start invisibly (index wrap) and keep running forever.
  const SLIDE_MS = 600;
  const DWELL_MS = 3000;

  const [index, setIndex] = useState(0);
  const [transitioning, setTransitioning] = useState(true);
  const [paused, setPaused] = useState(false);

  const count = allCelebrations.length;

  useEffect(() => {
    if (
      variant === "list" ||
      prefersReducedMotion ||
      paused ||
      slideWidth <= 0 ||
      count < 2
    ) {
      return;
    }

    // At the end of the first set we show the second set's first card; reset
    // to the start without a visible jump (both sets are identical), then move
    // straight on to the second card so every card gets the same dwell time.
    if (index >= count) {
      const timer = setTimeout(() => {
        setTransitioning(false);
        setIndex(0);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            setTransitioning(true);
            setIndex(1);
          })
        );
      }, DWELL_MS + SLIDE_MS);
      return () => clearTimeout(timer);
    }

    const timer = setTimeout(() => setIndex(index + 1), DWELL_MS);
    return () => clearTimeout(timer);
  }, [index, paused, prefersReducedMotion, slideWidth, count, variant]);

  const [wishModal, setWishModal] = useState<{
    open: boolean;
    type: "birthday" | "anniversary";
    targetUserId: string;
    targetName: string;
  }>({ open: false, type: "birthday", targetUserId: "", targetName: "" });

  const openWish = (
    type: "birthday" | "anniversary",
    item: IBirthdayItem | IAnniversaryItem
  ) => {
    setWishModal({
      open: true,
      type,
      targetUserId: item.userId,
      targetName: item.name || "Employee",
    });
  };

  const renderTile = (c: CelebrationItem) => {
    const TagIcon = c.tagIcon;
    const item: IBirthdayItem | IAnniversaryItem =
      c.type === "birthday"
        ? birthdays.find((b) => b.userId === c.userId)!
        : anniversaries.find((a) => a.userId === c.userId)!;

    return (
      <div
        style={{ background: c.bg }}
        className="h-full flex items-center gap-3 p-[13px_15px] rounded-[15px] min-h-[74px]"
      >
        <div
          className="w-[42px] h-[42px] rounded-md flex-none grid place-items-center text-white font-bold text-body-xs leading-none shadow-xs"
          style={{
            background: getAvatarGradient(c.name || "?"),
          }}
        >
          {c.initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="truncate text-subtitle font-bold text-text-primary">
            {c.name || "-"}
          </div>
          <div
            className="text-body-xs font-semibold flex items-center gap-1.5 mt-0.5"
            style={{ color: c.tagColor }}
          >
            <TagIcon className="w-3.5 h-3.5 flex-none" />
            <span className="truncate">{c.tag}</span>
          </div>
        </div>
        <button
          onClick={() => openWish(c.type, item)}
          className={`h-[34px] px-3.5 ${buttonRadiusClass} bg-gradient-to-r from-primary-700 to-primary-600 text-white text-body-xs font-bold whitespace-nowrap shadow-brand hover:shadow-brand-hover hover:-translate-y-0.5 active:translate-y-0 transition-all duration-150 flex-none border-none cursor-pointer`}
        >
          {buttonLabel}
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-surface border border-border-secondary rounded-card p-5 shadow-soft">
        <div className="flex items-center justify-center h-[160px]">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-600 border-t-transparent" />
        </div>
      </div>
    );
  }

  // Two identical copies of the set are rendered so the wrap-around reset is
  // invisible; reduced-motion users get a single, static row.
  const marqueeSlides = prefersReducedMotion
    ? allCelebrations
    : [...allCelebrations, ...allCelebrations];

  return (
    <div className="bg-surface border border-border-secondary rounded-card p-5 shadow-soft">
      <div className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <PartyPopper className="w-[18px] h-[18px] text-primary-600 flex-none" />
          <h3 className="text-heading-sm font-bold text-text-primary truncate">
            {title || (variant === "list" ? "Celebrations" : "Birthdays & Anniversaries")}
          </h3>
        </div>
        <span className="text-caption font-bold text-event-birthday bg-event-birthday-bg px-2.5 py-1 rounded-full whitespace-nowrap flex-none">
          {allCelebrations.length} this week
        </span>
      </div>

      {allCelebrations.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <PartyPopper className="w-7 h-7 text-text-tertiary mb-2" />
          <p className="text-body-sm font-bold text-text-primary m-0">
            No celebrations today
          </p>
          <p className="text-body-xs text-text-tertiary mt-0.5 mb-0">
            Birthdays and anniversaries will appear here
          </p>
        </div>
      ) : variant === "list" ? (
        /* ---- VERTICAL LIST FORMAT (ADMIN DASHBOARD) ---- */
        <div className="flex flex-col gap-2 max-h-[216px] overflow-y-auto pr-1 scrollbar-thin">
          {allCelebrations.map((c, idx) => {
            const TagIcon = c.tagIcon;
            const item: IBirthdayItem | IAnniversaryItem =
              c.type === "birthday"
                ? birthdays.find((b) => b.userId === c.userId)!
                : anniversaries.find((a) => a.userId === c.userId)!;

            return (
              <div
                key={c.userId ? `${c.type}-${c.userId}` : idx}
                style={{ background: c.bg }}
                className="flex items-center gap-3 p-3 rounded-xl min-h-[64px]"
              >
                <div
                  className="w-10 h-10 rounded-avatar flex-none grid place-items-center text-white font-bold text-body-xs leading-none shadow-xs"
                  style={{
                    background: getAvatarGradient(c.name || "?"),
                  }}
                >
                  {c.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="truncate text-subtitle font-bold text-text-primary">
                    {c.name || "-"}
                  </div>
                  <div
                    className="text-body-xs font-semibold flex items-center gap-1.5 mt-0.5"
                    style={{ color: c.tagColor }}
                  >
                    <TagIcon className="w-3.5 h-3.5 flex-none" />
                    <span className="truncate">{c.tag}</span>
                  </div>
                </div>
                <button
                  onClick={() => openWish(c.type, item)}
                  className={`h-8 px-3.5 ${buttonRadiusClass} bg-gradient-to-r from-primary-700 to-primary-600 text-white text-body-xs font-semibold whitespace-nowrap shadow-brand hover:shadow-brand-hover hover:-translate-y-0.5 active:translate-y-0 transition-all duration-150 flex-none border-none cursor-pointer`}
                >
                  {buttonLabel}
                </button>
              </div>
            );
          })}
        </div>
      ) : allCelebrations.length === 1 ? (
        /* ---- SINGLE CELEBRATION ---- */
        <div className="max-w-md">{renderTile(allCelebrations[0])}</div>
      ) : (
        /* ---- STEP-AND-PAUSE MARQUEE (pauses on hover, static for reduced motion) ---- */
        <div
          ref={rootRef}
          className="overflow-hidden"
          role="region"
          aria-label="Birthdays and anniversaries"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div
            className="marquee-track"
            style={{
              transform: `translateX(${-index * slideWidth}px)`,
              transition: transitioning
                ? `transform ${SLIDE_MS}ms cubic-bezier(.4,0,.2,1)`
                : "none",
            }}
          >
            {marqueeSlides.map((c, slideIndex) => (
              <div
                key={slideIndex}
                className="px-1.5"
                style={{ width: slideWidth, flex: "none" }}
              >
                {renderTile(c)}
              </div>
            ))}
          </div>
        </div>
      )}

      <WishModal
        isOpen={wishModal.open}
        onClose={() => setWishModal((prev) => ({ ...prev, open: false }))}
        type={wishModal.type}
        targetUserId={wishModal.targetUserId}
        targetName={wishModal.targetName}
      />
    </div>
  );
};

export default BirthdayAnniversaryCard;
